import { mergeDddAndLocalPhoneDigits } from '../../services/utils/stringUtils';
import { REGRAS_QUALIDADE } from './descricaoRegraQualidade';

export type LinhaResumoQualidade = {
  negociacao?: string | null;
  ddd?: string | null;
  numero?: string | null;
  chipCcid?: string | null;
  excluirDoContrato?: boolean | null;
};

export type ItemResumoQualidade = {
  rotulo: string;
  texto?: string;
  itens?: string[];
  tom: 'ok' | 'divergente' | 'neutro';
  descricao?: string;
};

function digitos(valor?: string | null): string {
  return (valor || '').replace(/\D/g, '');
}

/** DDD + 8. O 9 extra do celular some para casar número antigo e novo. */
export function chaveTelefoneBr(valor?: string | null): string {
  let numero = digitos(valor);
  if (!numero) return '';
  if (numero.startsWith('55') && numero.length >= 12) numero = numero.slice(2);
  if (numero.length === 11 && numero[2] === '9') return numero.slice(0, 2) + numero.slice(3);
  return numero;
}

function telefoneUtil(valor?: string | null): string {
  const numero = digitos(valor);
  return chaveTelefoneBr(numero).length >= 10 ? numero : '';
}

function estaNoConjunto(numero: string, conjunto: string[]): boolean {
  const chave = chaveTelefoneBr(numero);
  if (!chave) return false;
  return conjunto.some((candidato) => chaveTelefoneBr(candidato) === chave);
}

function juntar(itens: string[]): string {
  if (itens.length <= 1) return itens[0] || '';
  if (itens.length === 2) return `${itens[0]} e ${itens[1]}`;
  return `${itens.slice(0, -1).join(', ')} e ${itens[itens.length - 1]}`;
}

function ehRenegociacao(negociacao?: string | null): boolean {
  return (negociacao || '').toLowerCase().includes('renegoc');
}

function numeroDaLinha(linha: LinhaResumoQualidade): string {
  const ddd = digitos(linha.ddd);
  const numero = digitos(linha.numero);
  if (!numero) return '';
  return mergeDddAndLocalPhoneDigits(ddd, numero);
}

function unicos(valores: string[]): string[] {
  const vistos = new Set<string>();
  const saida: string[] = [];
  valores.forEach((valor) => {
    const chave = chaveTelefoneBr(valor) || valor;
    if (vistos.has(chave)) return;
    vistos.add(chave);
    saida.push(valor);
  });
  return saida;
}

function itemRenegociacao(
  linhas: LinhaResumoQualidade[],
  numerosP2b: string[],
  semEstrutura: boolean,
): ItemResumoQualidade {
  const renegociacao = linhas.filter((linha) => ehRenegociacao(linha.negociacao) && !linha.excluirDoContrato);
  if (renegociacao.length === 0) {
    return { rotulo: REGRAS_QUALIDADE.renegociacao.rotulo, texto: 'Este contrato não tem linha de renegociação.', tom: 'neutro' };
  }
  const comNumero = renegociacao.map(numeroDaLinha).filter((numero) => telefoneUtil(numero));
  if (comNumero.length === 0) {
    return { rotulo: REGRAS_QUALIDADE.renegociacao.rotulo, texto: 'As linhas de renegociação do contrato estão sem número.', tom: 'neutro' };
  }
  if (semEstrutura) {
    return { rotulo: REGRAS_QUALIDADE.renegociacao.rotulo, texto: 'O P2B está inadimplente e não trouxe as linhas.', tom: 'divergente' };
  }
  const ausentes = unicos(comNumero.filter((numero) => !estaNoConjunto(numero, numerosP2b)));
  if (ausentes.length === 0) {
    const texto = comNumero.length === 1
      ? 'A linha do contrato está no P2B.'
      : `As ${comNumero.length} linhas do contrato estão no P2B.`;
    return { rotulo: REGRAS_QUALIDADE.renegociacao.rotulo, texto, tom: 'ok' };
  }
  const verbo = ausentes.length === 1 ? 'não está' : 'não estão';
  return {
    rotulo: REGRAS_QUALIDADE.renegociacao.rotulo,
    texto: `${juntar(ausentes)} ${verbo} no P2B.`,
    tom: 'divergente',
  };
}

type OndeAdmin = 'ambos' | 'soP2b' | 'soLemit' | 'nenhum';

function fraseAdmin(telefones: string[], onde: OndeAdmin): string {
  const um = telefones.length === 1;
  const sujeito = um ? `O telefone ${telefones[0]}` : `Os telefones ${juntar(telefones)}`;
  const verbo = um ? 'está' : 'estão';
  const naoVerbo = um ? 'não foi encontrado' : 'não foram encontrados';
  if (onde === 'ambos') return `${sujeito} ${verbo} no P2B e no Lemmit.`;
  if (onde === 'soP2b') return `${sujeito} ${verbo} no P2B, mas ${naoVerbo} no Lemmit.`;
  if (onde === 'soLemit') return `${sujeito} ${naoVerbo} no P2B, mas ${verbo} no Lemmit.`;
  return `${sujeito} ${naoVerbo} no P2B e nem no Lemmit.`;
}

export function adminEncontradoNoP2b(entrada: {
  telefonesAdministrador?: Array<string | null | undefined>;
  linhasP2b?: Array<{ numero?: string | null }>;
  contatosP2b?: Array<string | null | undefined>;
}): boolean {
  const uteis = unicos((entrada.telefonesAdministrador ?? []).map((telefone) => telefoneUtil(telefone)).filter(Boolean));
  if (uteis.length === 0) return false;
  const numerosP2b = (entrada.linhasP2b ?? []).map((linha) => digitos(linha.numero)).filter(Boolean);
  const contatos = (entrada.contatosP2b ?? []).map((contato) => digitos(contato)).filter(Boolean);
  return uteis.every((telefone) => estaNoConjunto(telefone, numerosP2b) || estaNoConjunto(telefone, contatos));
}

function itemAdministrador(
  telefones: Array<string | null | undefined>,
  numerosP2b: string[],
  contatos: string[],
  semEstrutura: boolean,
  numerosLemit: string[],
  temResultadoLemit: boolean,
): ItemResumoQualidade {
  const uteis = unicos(telefones.map((telefone) => telefoneUtil(telefone)).filter(Boolean));
  if (uteis.length === 0) {
    return { rotulo: REGRAS_QUALIDADE.administrador.rotulo, texto: 'O contrato não tem telefone de administrador.', tom: 'neutro' };
  }
  if (semEstrutura) {
    return {
      rotulo: REGRAS_QUALIDADE.administrador.rotulo,
      texto: 'O P2B está inadimplente e não trouxe as linhas nem a fatura.',
      tom: 'divergente',
    };
  }
  const noP2b = (telefone: string) => estaNoConjunto(telefone, numerosP2b) || estaNoConjunto(telefone, contatos);
  const noLemit = (telefone: string) => estaNoConjunto(telefone, numerosLemit);
  if (!temResultadoLemit) {
    const presentes = uteis.filter(noP2b);
    if (presentes.length === uteis.length) {
      const texto = uteis.length === 1
        ? `O telefone ${uteis[0]} está no P2B.`
        : `Os telefones ${juntar(uteis)} estão no P2B.`;
      return { rotulo: REGRAS_QUALIDADE.administrador.rotulo, texto, tom: 'ok' };
    }
    const ausentes = uteis.filter((telefone) => !presentes.includes(telefone));
    const texto = ausentes.length === 1
      ? `O telefone ${ausentes[0]} não aparece nas linhas nem na fatura.`
      : `Os telefones ${juntar(ausentes)} não aparecem nas linhas nem na fatura.`;
    return { rotulo: REGRAS_QUALIDADE.administrador.rotulo, texto, tom: 'divergente' };
  }
  const grupos: Record<OndeAdmin, string[]> = { ambos: [], soP2b: [], soLemit: [], nenhum: [] };
  uteis.forEach((telefone) => {
    const p2b = noP2b(telefone);
    const lemit = noLemit(telefone);
    if (p2b && lemit) grupos.ambos.push(telefone);
    else if (p2b) grupos.soP2b.push(telefone);
    else if (lemit) grupos.soLemit.push(telefone);
    else grupos.nenhum.push(telefone);
  });
  const partes = (['ambos', 'soP2b', 'soLemit', 'nenhum'] as OndeAdmin[])
    .filter((onde) => grupos[onde].length > 0)
    .map((onde) => fraseAdmin(grupos[onde], onde));
  const tom = grupos.soLemit.length > 0 || grupos.nenhum.length > 0 ? 'divergente' : 'ok';
  return { rotulo: REGRAS_QUALIDADE.administrador.rotulo, texto: partes.join(' '), tom };
}

export function resumoQualidade(entrada: {
  linhasContrato?: LinhaResumoQualidade[];
  telefonesAdministrador?: Array<string | null | undefined>;
  linhasP2b?: Array<{ numero?: string | null }>;
  contatosP2b?: Array<string | null | undefined>;
  telefonesLemit?: Array<string | null | undefined>;
  temResultadoLemit?: boolean;
  inadimplente?: boolean;
  mesmoPedidoId?: string | null;
}): ItemResumoQualidade[] {
  const numerosP2b = (entrada.linhasP2b ?? []).map((linha) => digitos(linha.numero)).filter(Boolean);
  const contatos = (entrada.contatosP2b ?? []).map((contato) => digitos(contato)).filter(Boolean);
  const numerosLemit = (entrada.telefonesLemit ?? []).map((telefone) => digitos(telefone)).filter(Boolean);
  const semEstrutura = Boolean(entrada.inadimplente) && numerosP2b.length === 0 && contatos.length === 0;
  const itens = [
    itemRenegociacao(entrada.linhasContrato ?? [], numerosP2b, semEstrutura),
    itemAdministrador(
      entrada.telefonesAdministrador ?? [],
      numerosP2b,
      contatos,
      semEstrutura,
      numerosLemit,
      Boolean(entrada.temResultadoLemit),
    ),
  ];
  const mesmo = (entrada.mesmoPedidoId ?? '').trim();
  if (mesmo) {
    itens.push({
      rotulo: 'Pedido',
      texto: `O Aditivo e a Renegociação apontam para o mesmo pedido ${mesmo}.`,
      tom: 'divergente',
    });
  }
  return itens;
}
